import { useEffect, useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// material
import {
  Box,
  Button,
  Card,
  Container,
  Stack,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  Typography
} from '@mui/material';

// graphql
import { useQuery } from '@apollo/client';
import { UserGQLFunction } from '../graphql/USER';

// components
import Iconify from '../components/Iconify';
import Page from '../components/Page';
import Label from '../components/Label';
import Scrollbar from '../components/Scrollbar';
import SearchNotFound from '../sections/people/SearchNotFound';
import { ListHead, ListToolbar, LoadingList } from '../sections/people';
// import BasicBreadcrumb from '../components/BasicBreadcrumb';
import EmptyList from '../sections/people/EmptyList';

// info
import { USER } from '../info/USER';

// scss
import './Table.scss';

function TableContent({ data, page, rowsPerPage, variant = 'ghost', color = 'info' }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <TableBody>
      {data.map((user, index) => (
        <TableRow hover key={index} onClick={() => navigate(`/user/info/${user.id}`)}>
          <TableCell>{index + 1 + page * rowsPerPage}</TableCell>
          <TableCell align="left">{user.userId}</TableCell>
          <TableCell align="left">{user.email}</TableCell>
          <TableCell>
            <Typography variant="subtitle2" noWrap>
              {user.username}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="subtitle2" noWrap>
              {user.nickname}
            </Typography>
          </TableCell>
          <TableCell align={user.title ? 'left' : 'center'}>{user.title || '-'}</TableCell>
          <TableCell align="left">
            <Label variant="ghost" color={'default'}>
              {t(`common.${USER.USER_TYPE[user.userType]}`)}
            </Label>
          </TableCell>
          <TableCell align="left">
            <Label variant="ghost" color={!user.userState ? 'success' : 'warning'}>
              {t(`common.${USER.USER_STATE[user.userState]}`)}
            </Label>
          </TableCell>
          <TableCell align="left">
            <Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={1}>
              {user.deviceIds?.length ? (
                user.deviceIds.map((deviceId, index) => (
                  <Label variant={variant} color={color} key={index}>
                    <Box sx={{ overflowX: 'auto', whiteSpace: 'nowrap', width: '100%', maxWidth: 400, display: 'block' }}>
                      <Typography fontFamily="monospace">{deviceId}</Typography>
                    </Box>
                  </Label>
                ))
              ) : (
                <Typography variant="body1" gutterBottom>
                  -
                </Typography>
              )}
            </Stack>
          </TableCell>
          <TableCell align="left">
            <Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={1}>
              {user.pushIds?.length ? (
                user.pushIds.map((pushId, index) => (
                  <Label variant={variant} color={color} key={index}>
                    <Box sx={{ overflowX: 'auto', whiteSpace: 'nowrap', width: '100%', maxWidth: 400, display: 'block' }}>
                      <Typography fontFamily="monospace">{pushId}</Typography>
                    </Box>
                  </Label>
                ))
              ) : (
                <Typography variant="body1" gutterBottom>
                  -
                </Typography>
              )}
            </Stack>
          </TableCell>
          <TableCell align="left">
            <Button
              variant="text"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/user/history?userId=${user.userId}&id=${user.id}`);
              }}
            >
              {t('history.title')}
            </Button>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  );
}
function AllUserTable({ page, rowsPerPage, setTotal }) {
  const { loading, data } = useQuery(UserGQLFunction.GET_ALL_USERS, {
    variables: { page, rowsPerPage },
    fetchPolicy: 'network-only'
  });
  useEffect(() => {
    if (loading === false && data) {
      setTotal(data.allUsers.len);
    }
  }, [loading, data, setTotal]);
  if (loading) return <LoadingList TABLE_HEAD={USER.TABLE_HEAD} />;
  // console.log(data);
  if (!data?.allUsers.len)
    return (
      <TableBody>
        <TableRow>
          <TableCell align="center" colSpan={USER.TABLE_HEAD.length} sx={{ py: 3 }}>
            <EmptyList />
          </TableCell>
        </TableRow>
      </TableBody>
    );
  return <TableContent data={data.allUsers.users} page={page} rowsPerPage={rowsPerPage} />;
}
function SearchResultTable({ page, rowsPerPage, searchField, searchInput, setTotal }) {
  const { loading, data } = useQuery(UserGQLFunction.SEARCH_USERS, {
    variables: { page, rowsPerPage, searchField, searchStr: searchInput }
  });
  useEffect(() => {
    if (loading === false && data) {
      console.log(`data.searchUsers: ${JSON.stringify(data.searchUsers, null, 2)}`);
      setTotal(data.searchUsers.len);
    }
  }, [loading, data, setTotal]);
  if (loading) return <LoadingList TABLE_HEAD={USER.TABLE_HEAD} />;
  return data.searchUsers.len ? (
    <TableContent data={data.searchUsers.users} page={page} rowsPerPage={rowsPerPage} />
  ) : (
    <TableBody>
      <TableRow>
        <TableCell align="center" colSpan={USER.TABLE_HEAD.length} sx={{ py: 3 }}>
          <SearchNotFound searchQuery={searchInput} />
        </TableCell>
      </TableRow>
    </TableBody>
  );
}

export default function User() {
  const { t } = useTranslation();
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [searchField, setSearchField] = useState('username');
  const [searchInput, setSearchInput] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(15);

  useEffect(() => {
    setPage(0);
  }, [searchField, searchInput]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // const bread = [
  //  <Typography key="1" color="text.primary">
  //    Users
  //  </Typography>,
  // ];
  return (
    <Page title={t('nav.users')}>
      <Container maxWidth={false}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Stack direction="column">
            <Typography variant="h4" gutterBottom>
              {t('user.title')}
            </Typography>
          </Stack>
        </Stack>

        <Card>
          <ListToolbar
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            field={searchField}
            setSearchField={setSearchField}
            selection={USER.SEARCH_FIELDS.map(field => ({ id: field, label: t(`common.${field.toLowerCase()}`) }))}
          />

          <TablePagination
            rowsPerPageOptions={[15, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={t('common.rows_per_page')}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 0 }}>
              <Table>
                <ListHead headLabel={USER.TABLE_HEAD.map(head => ({ ...head, label: head.label ? t(`common.${head.label}`) : '' }))} />
                {searchInput ? (
                  <SearchResultTable
                    searchInput={searchInput}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    setTotal={setTotal}
                    searchField={searchField}
                  />
                ) : (
                  <AllUserTable rowsPerPage={rowsPerPage} page={page} setTotal={setTotal} />
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
        </Card>
      </Container>
    </Page>
  );
}
